var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { X as Close } from '@styled-icons/bootstrap/X';
import { device } from 'constants/breakpoints';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import Modal from 'styled-react-modal';
import { convertColorWithoutOpacity } from 'utils/color';
import { ButtonPrimary } from '../Button';
import FlexBox from '../Container/FlexBox';
import IconButton from '../IconButton';
import { GET_BATCH_DATA, GET_BATCH_SUMMARY } from 'graphql/queries';
import client from 'utils/graphql-client';
const StyledModal = Modal.styled `
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 464px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  background-color: ${({ theme }) => `${convertColorWithoutOpacity(theme.palette.primary.main, 0.1)}`} !important;
  position: relative;
  font-family: Lato;
  @media ${device.mobile} {
    max-width: 95vw;
  }
`;
const Title = styled.h3 `
  color: #000000;
  font-size: 16px;
  font-weight: 300;
  line-height: normal;
  letter-spacing: 1.6px;
  text-transform: uppercase;
`;
const Paragraph = styled.p `
  color: #000000;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;
const ErrorMessage = styled.p `
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  margin: 4px 0;
`;
const Label = styled.span `
  color: #000000;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16.8px;
  margin-bottom: 4px;
`;
const Content = styled.div `
  display: flex;
  flex-direction: column;
`;
const ActionButtonContainer = styled.div `
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  > :first-child {
    background: white !important;
    color: ${({ theme }) => theme.palette.grey.A800} !important;
  }
  > * {
    text-transform: none !important;
  }
`;
const DateInput = styled.input `
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.palette.grey[200]};
  padding: 4px 8px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  cursor: pointer;
  outline-color: ${({ theme }) => theme.palette.primary.main} !important;
  &:hover {
    outline: auto 1px;
  }
  &::-webkit-datetime-edit-day-field:focus,
  &::-webkit-datetime-edit-month-field:focus,
  &::-webkit-datetime-edit-year-field:focus {
    background-color: #dcd8d1;
  }
`;
const CloseButton = styled(IconButton) `
  position: absolute;
  right: 4px;
  top: 4px;
  color: ${({ theme }) => theme.palette.grey.A500};
`;
const StyledRawMaterialFilterItem = styled.div `
  display: inline-flex;
  padding: 8px;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: ${({ theme }) => theme.borderRadiusButton};
  background: ${({ theme }) => theme.palette.primary.main};
  font-weight: 700;
  height: 24px;
`;
const rotate360 = keyframes `
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
export const Spinner = styled.div `
  animation: ${rotate360} 1s linear infinite;
  transform: translateZ(0);
  border-top: 2px solid #f3f3f3;
  border-right: 2px solid #f3f3f3;
  border-bottom: 2px solid #f3f3f3;
  border-left: 2px solid ${({ theme }) => theme.palette.primary.main};
  background: transparent;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  transform: translate3d(-50%, -50%, 0);
`;
const Link = styled.div `
  text-decoration: underline;
  cursor: pointer;
  font-size: 12px;
  margin-top: 4px;
  width: fit-content;
`;
const DATE_FORMAT = 'YYYY-MM-DD';
const BatchTraceDialog = ({ open, productId, lang, latestBatch, onClose, onSubmit, }) => {
    const { t } = useTranslation();
    const latestBatchDate = moment(latestBatch.date)
        .utc(true)
        .format(DATE_FORMAT);
    const [dateValue, setDateValue] = useState(latestBatchDate);
    const [batchSummaries, setBatchSummaries] = useState([{ date: latestBatchDate, rawMaterials: latestBatch.rawMaterials }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const selectedBatchRawMaterials = useMemo(() => {
        var _a;
        return ((_a = batchSummaries.find(({ date }) => moment(date).utc(true).format(DATE_FORMAT) === dateValue)) === null || _a === void 0 ? void 0 : _a.rawMaterials) || [];
    }, [dateValue, batchSummaries]);
    // Reset state when dialog is opened again
    useEffect(() => {
        if (open) {
            setLoading(false);
            setError(null);
            setDateValue(moment(latestBatch.date).utc(true).format(DATE_FORMAT));
        }
    }, [open]);
    useEffect(() => {
        setError(undefined);
        handleDateChange(dateValue);
    }, [dateValue]);
    const handleSelectLatestDate = () => setDateValue(moment(latestBatch.date).utc(true).format(DATE_FORMAT));
    const handleDateChange = (newValue) => __awaiter(void 0, void 0, void 0, function* () {
        // Check if the batch summary for the selected date is already fetched
        if (batchSummaries.findIndex(({ date }) => date === newValue) === -1) {
            setLoading(true);
            try {
                const batchSummary = yield client.request(GET_BATCH_SUMMARY, {
                    productId,
                    lang,
                    batchTracingDate: newValue,
                });
                setBatchSummaries((prev) => [
                    ...prev,
                    {
                        date: newValue,
                        rawMaterials: batchSummary.productBatchData.traceableRawMaterials,
                    },
                ]);
            }
            catch (e) {
                setError(t('existing-batch-error'));
            }
            finally {
                setLoading(false);
            }
        }
    });
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const batchData = yield client.request(GET_BATCH_DATA, {
                productId,
                lang,
                batchTracingDate: dateValue,
            });
            onSubmit(batchData.productBatchData);
        }
        catch (e) {
            setError(t('existing-batch-error'));
        }
        finally {
            setLoading(false);
        }
    });
    return (React.createElement(StyledModal, { isOpen: open, onBackgroundClick: onClose, onEscapeKeydown: onClose },
        React.createElement(Title, null, t('trace-my-batch')),
        React.createElement(CloseButton, { onClick: onClose },
            React.createElement(Close, { width: 24 })),
        React.createElement(Content, null,
            !!selectedBatchRawMaterials.length && (React.createElement(FlexBox, { gap: 1, mb: 2, flexWrap: "wrap" }, selectedBatchRawMaterials.map(({ id, title }) => (React.createElement(StyledRawMaterialFilterItem, { key: id }, title))))),
            React.createElement(Paragraph, null, t('batch-trace-dialog-paragraph')),
            React.createElement(Label, null, t('best-before-date')),
            React.createElement(DateInput, { value: dateValue, type: "date", onChange: (event) => setDateValue(event.target.value) }),
            error && React.createElement(ErrorMessage, null, error),
            dateValue !== latestBatchDate && (React.createElement(Link, { onClick: handleSelectLatestDate }, t('use-latest-date')))),
        React.createElement(ActionButtonContainer, null,
            React.createElement(ButtonPrimary, { onClick: onClose }, t('cancel')),
            React.createElement(ButtonPrimary, { disabled: Boolean(loading || !selectedBatchRawMaterials.length), onClick: handleSubmit },
                React.createElement(FlexBox, { gap: 1 },
                    loading && React.createElement(Spinner, null),
                    t('show-details'))))));
};
export default BatchTraceDialog;
